import { useEffect, useState } from "react";
import { Row, Col, Form, Input, Button, Spin, Upload, Badge } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { displayMessage, docImageRender, postAPI } from "../../../utils/common";
import {
  ERROR_MSG_TYPE,
  SUCCESS_MSG_TYPE,
  SUBFAILED,
} from "../../../constants/messages";
import {
  FILE_UPLOAD,
  DELETE_VENDOR_DOCUMENTS,
  MANAGE_VENDOR_DOCUMENTS,
} from "../../../constants/api";

const { Dragger } = Upload;

function VendorDocumentsTab({
  vendor,
  edit_id,
  handleTabChange,
  fetchVendorDetail,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /* ================= STATE ================= */

  const [docImages, setDocImages] = useState({
    doc_pan_card_img: null,
    doc_aadhar_front_img: null,
    doc_aadhar_back_img: null,
    doc_msme_certificate_img: null,
    doc_bank_passbook: null,
    doc_gst_certificate_img: null,
    doc_trade_license: null,
    doc_hotel_profile_img: null,
  });

  const [fileLists, setFileLists] = useState({
    doc_pan_card_img: [],
    doc_aadhar_front_img: [],
    doc_aadhar_back_img: [],
    doc_msme_certificate_img: [],
    doc_bank_passbook: [],
    doc_gst_certificate_img: [],
    doc_trade_license: [],
    doc_hotel_profile_img: [],
  });

  /* ================= INIT ================= */

  useEffect(() => {
    if (!vendor?.documents) return;

    form.setFieldsValue({
      vendor_id: vendor.id,
      id: vendor.documents.id,
      doc_pan_card_no: vendor.documents.doc_pan_card_no,
      doc_aadhar_no: vendor.documents.doc_aadhar_no,
      doc_property_img_google_drive_link:
        vendor.documents.doc_property_img_google_drive_link,
    });

    setDocImages({
      doc_pan_card_img: vendor.documents.pan_image?.filePath || null,
      doc_aadhar_front_img:
        vendor.documents.aadhar_front_image?.filePath || null,
      doc_aadhar_back_img: vendor.documents.aadhar_back_image?.filePath || null,
      doc_msme_certificate_img: vendor.documents.msme_image?.filePath || null,
      doc_bank_passbook: vendor.documents.passbook_image?.filePath || null,
      doc_gst_certificate_img: vendor.documents.gst_image?.filePath || null,
      doc_trade_license: vendor.documents.trade_image?.filePath || null,
      doc_hotel_profile_img:
        vendor.documents.hotel_profile_image?.filePath || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  /* ================= UPLOAD ================= */

  const handleUpload = (fieldName) => async () => {
    if (!fileLists[fieldName].length) {
      displayMessage(ERROR_MSG_TYPE, "No file selected");
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append("folder", "Vendor-Documents");
    fileLists[fieldName].forEach((file) => data.append("files[]", file));
    data.append(
      "tag",
      `${vendor.vendor_name} ${vendor.hotel_name} (${vendor.id})`,
    );

    postAPI(
      FILE_UPLOAD,
      data,
      (res) => {
        if (res.status) {
          const file = res.responses[0];
          setDocImages((prev) => ({ ...prev, [fieldName]: file.filePath }));
          form.setFieldsValue({ [fieldName]: file.id });
          setFileLists((prev) => ({ ...prev, [fieldName]: [] }));
          form.submit();
          displayMessage(SUCCESS_MSG_TYPE, res.message);
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Upload failed");
      },
    );
  };

  /* ================= DELETE ================= */

  const deleteDoc = (fieldName, docId) => {
    setLoading(true);
    postAPI(
      DELETE_VENDOR_DOCUMENTS,
      {
        vendor_document_id: vendor.documents.id,
        doc_key: fieldName,
        image_id: docId,
      },
      (res) => {
        setLoading(false);
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          setDocImages((prev) => ({ ...prev, [fieldName]: null }));
          form.setFieldsValue({ [fieldName]: null });
          fetchVendorDetail();
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Delete failed");
      },
    );
  };

  /* ================= UPLOAD CONFIG ================= */

  const getDraggerProps = (fieldName) => ({
    height: 200,
    multiple: false,
    fileList: fileLists[fieldName],
    beforeUpload: (file) => {
      const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
      if (!allowed.includes(file.type)) {
        displayMessage(ERROR_MSG_TYPE, "Invalid file type");
        return Upload.LIST_IGNORE;
      }
      setFileLists((prev) => ({ ...prev, [fieldName]: [file] }));
      return false;
    },
    onRemove: () => setFileLists((prev) => ({ ...prev, [fieldName]: [] })),
  });

  /* ================= SUBMIT ================= */

  const onSubmit = (values) => {
    setLoading(true);
    postAPI(
      MANAGE_VENDOR_DOCUMENTS,
      { ...values, vendor_id: vendor.id },
      (res) => {
        setLoading(false);
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          fetchVendorDetail();
          handleTabChange("1");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, SUBFAILED);
      },
    );
  };

  /* ================= DOCUMENT BLOCK ================= */

  const DocumentBlock = ({ field, label, doc, status, remark }) => (
    <Badge.Ribbon
      text={status?.toUpperCase()}
      color={
        status === "approved" ? "green" : status === "reject" ? "red" : "orange"
      }
    >
      <Form.Item name={field} label={label}>
        {docImages[field] ? (
          <>
            {docImageRender(label, docImages[field])}
            {status === "reject" && remark && (
              <div
                style={{
                  marginTop: 10,
                  padding: 8,
                  background: "#fff1f0",
                  border: "1px solid #ffa39e",
                  borderRadius: 6,
                  color: "#cf1322",
                  fontSize: 13,
                }}
              >
                <strong>Rejection Reason:</strong>
                <div>{remark}</div>
              </div>
            )}

            <Button
              danger
              block
              style={{ marginTop: 8 }}
              onClick={() => deleteDoc(field, doc?.id)}
            >
              Delete
            </Button>
          </>
        ) : (
          <>
            <Dragger {...getDraggerProps(field)}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p>Click or drag file</p>
            </Dragger>
            <Button
              type="primary"
              block
              style={{ marginTop: 8 }}
              onClick={handleUpload(field)}
            >
              Upload
            </Button>
          </>
        )}
      </Form.Item>
    </Badge.Ribbon>
  );

  /* ================= JSX ================= */

  return (
    <Spin spinning={loading}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="id"
          initialValue={vendor?.documents?.id ?? null}
          hidden
        />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Form.Item
              name="doc_pan_card_no"
              label="PAN Card Number"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>
            <DocumentBlock
              field="doc_pan_card_img"
              label="PAN Card Image"
              doc={vendor?.documents?.pan_image}
              status={vendor?.documents?.doc_pan_card_is_approved}
              remark={vendor?.documents?.doc_pan_card_remark}
            />
          </Col>

          <Col xs={24} md={18}>
            <Form.Item
              name="doc_aadhar_no"
              label="Aadhar Card Number"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <DocumentBlock
                  field="doc_aadhar_front_img"
                  label="Aadhar Front"
                  doc={vendor?.documents?.aadhar_front_image}
                  status={vendor?.documents?.doc_aadhar_is_approved}
                  remark={vendor?.documents?.doc_aadhar_is_approved}
                />
              </Col>
              <Col xs={24} md={12}>
                <DocumentBlock
                  field="doc_aadhar_back_img"
                  label="Aadhar Back"
                  doc={vendor?.documents?.aadhar_back_image}
                  status={vendor?.documents?.doc_aadhar_is_approved}
                  remark={vendor?.documents?.doc_aadhar_remark}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <DocumentBlock
              field="doc_msme_certificate_img"
              label="MSME Certificate"
              doc={vendor?.documents?.msme_image}
              status={vendor?.documents?.doc_msme_certificate_is_approved}
              remark={vendor?.documents?.doc_msme_certificate_remark}
            />
          </Col>
          <Col xs={24} md={6}>
            <DocumentBlock
              field="doc_bank_passbook"
              label="Bank Passbook"
              doc={vendor?.documents?.passbook_image}
              status={vendor?.documents?.doc_bank_passbook_is_approved}
              remark={vendor?.documents?.doc_bank_passbook_remark}
            />
          </Col>
          <Col xs={24} md={6}>
            <DocumentBlock
              field="doc_gst_certificate_img"
              label="GST Certificate"
              doc={vendor?.documents?.gst_image}
              status={vendor?.documents?.doc_gst_certificate_is_approved}
              remark={vendor?.documents?.doc_gst_certificate_remark}
            />
          </Col>
          <Col xs={24} md={6}>
            <DocumentBlock
              field="doc_trade_license"
              label="Trade License"
              doc={vendor?.documents?.trade_image}
              status={vendor?.documents?.doc_trade_license_is_approved}
              remark={vendor?.documents?.doc_trade_license_remark}
            />
          </Col>
          <Col xs={24} md={6}>
            <DocumentBlock
              field="doc_hotel_profile_img"
              label="Hotel Profile"
              doc={vendor?.documents?.hotel_profile_image}
              status={vendor?.documents?.doc_hotel_profile_is_approved}
              remark={vendor?.documents?.doc_hotel_profile_remark}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={24}>
            <Badge.Ribbon
              text={vendor?.documents?.doc_property_img_google_drive_link_is_approved?.toUpperCase()}
              color={
                vendor?.documents
                  ?.doc_property_img_google_drive_link_is_approved ===
                "approved"
                  ? "green"
                  : vendor?.documents
                        ?.doc_property_img_google_drive_link_is_approved ===
                      "reject"
                    ? "red"
                    : "orange"
              }
            >
              <Form.Item
                name="doc_property_img_google_drive_link"
                label="Property Image Drive Link"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </Badge.Ribbon>
            {vendor?.documents
              ?.doc_property_img_google_drive_link_is_approved === "reject" &&
              vendor?.documents?.doc_property_img_google_drive_link_remark && (
                <div
                  style={{
                    marginTop: 10,
                    marginBottom: 10,
                    padding: 8,
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 6,
                    color: "#cf1322",
                    fontSize: 13,
                  }}
                >
                  <strong>Rejection Reason:</strong>
                  <div>
                    {
                      vendor?.documents
                        ?.doc_property_img_google_drive_link_remark
                    }
                  </div>
                </div>
              )}
          </Col>

          <Col span={24}>
            <Form.Item
              style={{ float: "right", marginTop: 20 }}
              label=""
              colon={false}
            >
              <Button type="primary" block htmlType="submit" loading={loading}>
                {edit_id ? "Update" : "Submit"}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
}

export default VendorDocumentsTab;
